import { GATEWAY_API_URL } from '@admin-panel/config/env';
import { getDeviceFingerprint } from '@admin-panel/utils';
import { runningRelease } from '../releaseWatcher/ReleaseWatcher';
import {
  eventsUrl,
  parseLastReport,
  shouldReport,
  stateSignature,
  type SetupStateParams,
  type AlertShownParams,
  type SetupTestResult,
} from './setupTelemetryRules';

/**
 * Sends Hub's order-alert setup events (and how each alert was presented) to Gateway's analytics endpoint (ClickHouse
 * `events.raw_lk_events`, read by Metabase). Fire and forget: never blocks, retries or surfaces
 * an error. Deployed builds only (Stage, Production), so local development never reports.
 *
 * The operator is identified in `event_params.erp_user_id` (client-reported, like rider-android's
 * location pings). No sign-in token, no top-level user or customer id, no vendor header.
 */

/** A random id per installation (survives logout), so devices are counted even if fingerprints collide. */
export const INSTALL_ID_KEY = 'hub-install-id';
const LAST_REPORT_KEY = 'hub-setup-report';

export const readInstallId = () => {
  try {
    const existing = localStorage.getItem(INSTALL_ID_KEY);
    if (existing) return existing;
    const created = crypto.randomUUID();
    localStorage.setItem(INSTALL_ID_KEY, created);
    return created;
  } catch {
    return 'unavailable';
  }
};

/** The operator signed in on this device, kept for test-result events. */
let currentErpUserId: number | null = null;
export const setTelemetryUser = (id: number | null) => {
  currentErpUserId = id;
};

const send = (
  eventName: string,
  params: Record<string, unknown>,
  pageName: 'alerts_setup' | 'alerts' = 'alerts_setup',
) => {
  const url = eventsUrl(GATEWAY_API_URL);
  const release = runningRelease.value;
  if (!url || !release || typeof fetch !== 'function') return;
  const deviceId = getDeviceFingerprint();
  void fetch(url, {
    method: 'POST',
    keepalive: true,
    headers: {
      'Content-Type': 'application/json',
      'x-lk-portalcode': 'erp',
      'x-lk-app': 'hub',
      'x-lk-version': release,
      ...(deviceId ? { 'x-lk-deviceid': deviceId } : {}),
    },
    body: JSON.stringify({
      events: [
        {
          event_id: crypto.randomUUID(),
          event_name: eventName,
          event_ts: new Date().toISOString(),
          page_type: 'hub',
          page_name: pageName,
          event_params: params,
        },
      ],
    }),
  }).catch(() => undefined);
};

/** Reports this device's setup state when it changed, and otherwise every few hours. */
export const reportSetupState = (params: SetupStateParams, now = Date.now()) => {
  const signature = stateSignature(params);
  let last = null;
  try {
    last = parseLastReport(localStorage.getItem(LAST_REPORT_KEY));
  } catch {
    last = null;
  }
  if (!shouldReport(signature, last, now)) return false;
  try {
    localStorage.setItem(LAST_REPORT_KEY, JSON.stringify({ signature, at: now }));
  } catch {
    // Storage blocked: the next check reports again, which is harmless.
  }
  send('hub_alert_setup_state', params);
  return true;
};

/** The operator's answer to the sound test (or that the test alert never showed). */
export const reportSetupTest = (
  result: SetupTestResult,
  device: { platform: string; browser: string },
) =>
  send('hub_alert_setup_test', {
    install_id: readInstallId(),
    erp_user_id: currentErpUserId,
    result,
    platform: device.platform,
    browser: device.browser,
  });

/**
 * How this Hub window presented an order alert (rang, chimed for another facility, or only on
 * the bell), once per alert per window. Several open tabs each report: count per install_id.
 */
export const reportAlertShown = (params: Omit<AlertShownParams, 'install_id' | 'erp_user_id'>) =>
  send(
    'hub_alert_shown',
    { install_id: readInstallId(), erp_user_id: currentErpUserId, ...params },
    'alerts',
  );
